import { useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar, TopBar, type NavItem, type NavChild } from '@kss/ui';
import { SessionEndedBanner, SessionTimeoutModal } from '@kss/auth';
import { useDynamicMenu, type SidebarNode } from './useDynamicMenu';

const NO_MENUS_ASSIGNED_MESSAGE =
  'No menus have been assigned to your role — please contact an administrator.';

/**
 * Backoffice shell. The sidebar tree is sourced live from the API:
 *
 *   GET /api/Menu/all      → every menu node for the tenant
 *   GET /api/RoleMenu/all  → which roles can see which menus
 *
 * Fallback rules
 * --------------
 * Admin users (Administrator / SuperAdmin / TenantAdmin / Owner) always
 * see every active menu; when the API returns nothing (offline mode,
 * un-seeded tenant, first-launch fresh install) they fall back to the
 * static `sidebarNav.json` baked into `@kss/ui` so the panel is never
 * empty for the person who's supposed to fix it.
 *
 * Non-admin users are gated strictly: an empty RoleMenu grant set
 * yields an empty sidebar with a "no menus assigned" message. The
 * static fallback is deliberately NOT shown to non-admins because it
 * would surface menus they aren't authorised to visit — the moment
 * they clicked one the backend would 403, and the UI would look broken.
 */
export function PanelLayout() {
  const { nodes, isAdmin, hasMenusAssigned } = useDynamicMenu();

  // SidebarNode (live API shape) → NavItem (UI package shape).
  //
  // Return shapes signal three different scenarios to <Sidebar>:
  //   - non-empty array → render exactly these items
  //   - empty array + emptyMessage → show "no menus assigned" state
  //   - undefined      → static config fallback (admin only)
  const navItems = useMemo<NavItem[] | undefined>(() => {
    if (nodes && nodes.length > 0) return nodes.map(toNavItem);
    // Admin with no dynamic data → let Sidebar fall back to its
    // static config. Non-admin with no grants → return an empty
    // array so Sidebar renders the emptyMessage panel.
    return isAdmin ? undefined : [];
  }, [nodes, isAdmin]);

  const emptyMessage =
    !isAdmin && !hasMenusAssigned ? NO_MENUS_ASSIGNED_MESSAGE : undefined;

  return (
    <div className="flex h-screen overflow-hidden bg-[var(--color-bg-app)] text-[var(--color-text-primary)]">
      <Sidebar items={navItems} emptyMessage={emptyMessage} />
      <div className="flex flex-1 flex-col overflow-hidden">
        <SessionEndedBanner />
        <TopBar />
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
      {/* Inactivity-warning modal — only renders when authenticated AND
          idle for IDLE_WARN_AFTER_MS. Bug ref: AI Agent 2026-06-11. */}
      <SessionTimeoutModal />
    </div>
  );
}

function toNavItem(node: SidebarNode): NavItem {
  return {
    id: node.id,
    label: node.name,
    labelKey: `nav.${node.name}`,
    icon: node.icon ?? 'Square',
    path: node.path,
    children: node.children?.map(toNavChild),
  };
}

function toNavChild(node: SidebarNode): NavChild {
  return {
    id: node.id,
    label: node.name,
    labelKey: `nav.${node.name}`,
    path: node.path ?? '#',
  };
}

export default PanelLayout;
